import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IEducationBackground } from '../../utils/types';

const name = 'educationBackgrounds';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<EducationBackgroundState>) =>
    createExtraReducers(builder),
});

export interface EducationBackgroundState {
  treatmentPlanId: number | null;
  editingEducationBackground: IEducationBackground | null;
  loading: boolean;
  error: any;
  errorCounts: number | null;
}

type EducationBackgroundStateUpdate = {
  [Key in keyof EducationBackgroundState]: { key: Key; data: EducationBackgroundState[Key] };
}[keyof EducationBackgroundState];

function createInitialState(): EducationBackgroundState {
  return {
    treatmentPlanId: null,
    editingEducationBackground: null,
    loading: false,
    error: null,
    errorCounts: null,
  };
}

function createReducers() {
  return {
    clearState,
    updateState,
  };

  function clearState(state: EducationBackgroundState, _action: any) {
    state.treatmentPlanId = null;
    state.editingEducationBackground = null;
    state.loading = false;
    state.error = null;
  }

  function updateState(
    state: EducationBackgroundState,
    action: PayloadAction<EducationBackgroundStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getEducationBackground: getEducationBackground(),
    createEducationBackground: createEducationBackground(),
    updateEducationBackground: updateEducationBackground(),
  };

  function getEducationBackground() {
    return createAsyncThunk(
      `${name}/getEducationBackground`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/education_background/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createEducationBackground() {
    return createAsyncThunk(
      `${name}/createEducationBackground`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/education_background/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateEducationBackground() {
    return createAsyncThunk(
      `${name}/updateEducationBackground`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/education_background/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<EducationBackgroundState>) {
  const { getEducationBackground, createEducationBackground, updateEducationBackground } =
    extraActions;

  builder
    // getEducationBackground()
    .addCase(getEducationBackground.pending, (state: EducationBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getEducationBackground.fulfilled, (state: EducationBackgroundState, action: any) => {
      state.loading = false;
      const currentEducationBackground = state.editingEducationBackground;
      if (Object.keys(currentEducationBackground ?? {}).length > 0) {
        state.editingEducationBackground =
          currentEducationBackground && !currentEducationBackground?.id
            ? state.editingEducationBackground
            : action.payload;
      } else {
        state.editingEducationBackground = action.payload;
      }
    })
    .addCase(getEducationBackground.rejected, (state: EducationBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    // createEducationBackground()
    .addCase(createEducationBackground.pending, (state: EducationBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createEducationBackground.fulfilled,
      (state: EducationBackgroundState, action: any) => {
        state.loading = false;
        state.editingEducationBackground = action.payload;
      },
    )
    .addCase(createEducationBackground.rejected, (state: EducationBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateEducationBackground()
    .addCase(updateEducationBackground.pending, (state: EducationBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateEducationBackground.fulfilled,
      (state: EducationBackgroundState, action: any) => {
        state.loading = false;
        state.editingEducationBackground = action.payload;
      },
    )
    .addCase(updateEducationBackground.rejected, (state: EducationBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const educationBackgroundActions = { ...slice.actions, ...extraActions };
export const educationBackgroundReducer = slice.reducer;
